import { useStore } from "@/store";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { Button } from "../Button";
import { useToast } from "../Toast";

export const VerificationSettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const profile = useStore.use.profile();
  const fetchProfile = useStore.use.fetchProfile();
  const isProfileLoading = useStore.use.isProfileLoading();

  const handleRefresh = useCallback(async () => {
    try {
      await fetchProfile({ force: true });
    } catch (err) {
      console.error("[settings-verification] Failed to refresh profile", { error: err });
      toast({ variant: "destructive", title: "Failed to refresh verification status" });
    }
  }, [fetchProfile, toast]);

  const email = profile?.email ?? "";
  const phone = profile?.phone_number ?? "";
  const emailVerified = Boolean(profile?.email_verified);
  const phoneVerified = Boolean(profile?.phone_verified);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold">Verification</h3>
        <p className="text-sm text-muted-foreground">Manage verification for your email and phone number.</p>
      </div>

      <div className="space-y-4">
        <div className="rounded-lg border bg-background p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Email</div>
              <div className="mt-1 break-all text-sm text-muted-foreground">{email || "No email"}</div>
              <div className="mt-2 text-xs">
                Status:{" "}
                <span className={emailVerified ? "text-emerald-700" : "text-amber-700"}>
                  {emailVerified ? "Verified" : "Not verified"}
                </span>
              </div>
            </div>
            {!emailVerified && (
              <Button
                type="button"
                onClick={() =>
                  void navigate({
                    to: "/auth/verify-otp",
                    search: { mode: "email", phone: undefined, email: undefined, from: undefined },
                  })
                }
              >
                Verify email
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-lg border bg-background p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Phone</div>
              <div className="mt-1 break-all text-sm text-muted-foreground">{phone || "No phone number"}</div>
              <div className="mt-2 text-xs">
                Status:{" "}
                <span className={phoneVerified ? "text-emerald-700" : "text-amber-700"}>
                  {phoneVerified ? "Verified" : "Not verified"}
                </span>
              </div>
            </div>
            {!phoneVerified && (
              <Button
                type="button"
                onClick={() => {
                  if (!phone.trim()) {
                    void navigate({ to: "/auth/edit-number" });
                    return;
                  }
                  void navigate({
                    to: "/auth/verify-otp",
                    search: { mode: "phone", phone: undefined, email: undefined, from: undefined },
                  });
                }}
              >
                {phone.trim() ? "Verify phone" : "Add phone"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => void handleRefresh()}
          disabled={isProfileLoading}
        >
          {isProfileLoading ? "Refreshing..." : "Refresh status"}
        </Button>
      </div>
    </div>
  );
};
